import type { ApiResponse, I18nMessageObject } from '@_/types';
import type { HTMLMotionProps } from 'motion/react';
import type { EmailStepProps } from './steps/EmailStep.tsx';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useState } from 'react';
import { ErrorCode } from '@_/constants';
import { MOTION_DURATION_SECONDS, MOTION_EASING } from '@_/theme/constants.ts';
import { isApiError } from '@_/utils-frontend';
import { EmailStep } from './steps/EmailStep.tsx';
import { OTPStep } from './steps/OTPStep.tsx';
import { ProfileStep } from './steps/ProfileStep.tsx';

const animationProps: HTMLMotionProps<'div'> = {
  initial: 'enter',
  animate: 'default',
  exit: 'exit',
  variants: {
    enter: () => {
      return {
        x: '24px',
        opacity: 0
      };
    },
    default: {
      x: 0,
      opacity: 1,
      transition: {
        duration: MOTION_DURATION_SECONDS.base,
        ease: MOTION_EASING.enter,
        type: 'tween'
      }
    },
    exit: () => {
      return {
        x: '-24px',
        opacity: 0,
        transition: {
          duration: MOTION_DURATION_SECONDS.fast,
          ease: MOTION_EASING.exit,
          type: 'tween'
        }
      };
    }
  }
};

type AuthFlowStep = 'email' | 'otp' | 'profile';

export function AuthFlow({
  endpoints,
  onSuccess,
  privacyPolicyUrl,
  termsOfUseUrl
}: Pick<EmailStepProps, 'privacyPolicyUrl' | 'termsOfUseUrl'> & {
  endpoints: {
    initAuth: () => Promise<void>;
    requestOTP: (data: { email: string }) => Promise<ApiResponse<{ canResendAt: string }>>;
    updateProfile?: (data: { name?: I18nMessageObject }) => Promise<unknown>;
    verifyOTP: (data: { email: string; otp: string }) => Promise<{ isNewUser: boolean }>;
  };
  onSuccess: (data: { email: string; name?: I18nMessageObject }) => void;
}) {
  const [step, setStep] = useState<AuthFlowStep>('email');
  const shouldReduceMotion = useReducedMotion();

  const resolvedAnimationProps = {
    ...animationProps,
    exit: shouldReduceMotion ? undefined : animationProps.exit,
    initial: shouldReduceMotion ? false : animationProps.initial
  };

  const [canResendAt, setCanResendAt] = useState<DateTime | null>(null);
  const [email, setEmail] = useState('');

  const requestOTPMutation = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      await endpoints.initAuth();
      return await endpoints.requestOTP({ email: email.trim() });
    },
    onError: error => {
      if (isApiError(error) && error.code === ErrorCode.ResendEmailRateLimit) {
        // Update resend date if OTP is being rate-limited
        setCanResendAt(DateTime.fromISO(error.data.resendAt));
      }
    },
    onSuccess: resp => {
      setCanResendAt(DateTime.fromISO(resp.data.canResendAt));
    }
  });

  const emailNextStep = async (data: { email: string }) => {
    await requestOTPMutation.mutateAsync({ email: data.email });
    setEmail(data.email.trim());
    setStep('otp');
  };

  const otpNextStep = async (data: { otp: string }) => {
    // Verify OTP
    const resp = await endpoints.verifyOTP({ email, otp: data.otp });

    if (resp.isNewUser) {
      // Proceed to profile step if signing up for a new account
      setStep('profile');
    } else {
      onSuccess({ email });
    }
  };

  const profileNextStep = async (data: { name: I18nMessageObject }) => {
    // Update user's name
    await endpoints.updateProfile?.({ name: data.name });

    onSuccess({ email, name: data.name });
  };

  return (
    <div>
      <AnimatePresence initial={false} mode="popLayout">
        {step === 'email' && (
          <motion.div key="email" {...resolvedAnimationProps}>
            <EmailStep
              onNext={emailNextStep}
              privacyPolicyUrl={privacyPolicyUrl}
              termsOfUseUrl={termsOfUseUrl}
            />
          </motion.div>
        )}

        {step === 'otp' && (
          <motion.div key="otp" {...resolvedAnimationProps}>
            <OTPStep
              canResendAt={canResendAt}
              email={email}
              onNext={otpNextStep}
              requestOTPMutation={requestOTPMutation}
            />
          </motion.div>
        )}

        {step === 'profile' && (
          <motion.div key="profile" {...resolvedAnimationProps}>
            <ProfileStep email={email} onNext={profileNextStep} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
